import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, Copy, MessageCircle, Package } from "lucide-react";
import toast from "react-hot-toast";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SpinnerBlock } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  getTikTokCollaboration,
  getTikTokCollaborationLink,
  getTikTokCreatorPromotion,
  type TikTokCreatorPick,
} from "@/services/tiktokShop.service";

import { errorMessage, shortDate } from "../tiktokFormat";

function CreatorPromotion({ collaborationId, openId }: { collaborationId: string; openId: string }) {
  const promotion = useQuery({
    queryKey: ["tiktok-collab-promotion", collaborationId, openId],
    queryFn: () => getTikTokCreatorPromotion(collaborationId, openId),
  });
  if (promotion.isLoading) return <SpinnerBlock className="py-4" />;
  if (promotion.error) return <p className="py-2 text-xs text-muted-foreground">{errorMessage(promotion.error)}</p>;
  return (
    <ul className="space-y-1.5 py-2">
      {promotion.data?.products.map((p) => (
        <li key={p.productId} className="flex items-center gap-2 text-xs">
          <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", p.added ? "bg-emerald-500" : "bg-muted-foreground/40")} />
          <span className="min-w-0 flex-1 truncate text-foreground">{p.title}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {p.added ? `${p.videoCount} video${p.videoCount === 1 ? "" : "s"} · ${p.liveCount} LIVE` : "Not added yet"}
            {p.commission !== null ? ` · ${p.commission}%` : ""}
            {!p.commissionEffective ? " (another campaign’s rate applies)" : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** One collab invite: its offer and what each invited creator did with it. */
export function InviteDetailSheet({
  id,
  onOpenChange,
  onMessage,
}: {
  id: string | null;
  onOpenChange: (open: boolean) => void;
  onMessage: (creator: TikTokCreatorPick) => void;
}) {
  const detail = useQuery({ queryKey: ["tiktok-collab", id], queryFn: () => getTikTokCollaboration(id!), enabled: id !== null });
  const [expanded, setExpanded] = useState<string | null>(null);
  const link = useMutation({
    mutationFn: () => getTikTokCollaborationLink(id!),
    onSuccess: async ({ link }) => {
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Invite link copied");
      } catch {
        toast(link, { duration: 8000 });
      }
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const c = detail.data;

  return (
    <Sheet open={id !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        {!detail.data && <SheetTitle className="sr-only">Collab invite</SheetTitle>}
        {detail.isLoading && <SpinnerBlock className="py-24" />}
        {detail.error && <p className="py-24 text-center text-sm text-muted-foreground">{errorMessage(detail.error)}</p>}
        {c && (
          <div className="space-y-6">
            <SheetHeader className="space-y-2 text-left">
              <SheetTitle>{c.name}</SheetTitle>
              <SheetDescription>
                {shortDate(c.startTime)} to {shortDate(c.endTime)}
                {c.freeSample ? (c.sampleApprovalExempt ? " · free samples, auto-approved" : " · free samples, you review") : " · no free samples"}
              </SheetDescription>
              <div>
                <Button type="button" size="sm" variant="outline" disabled={link.isPending} onClick={() => link.mutate()}>
                  <Copy className="h-3.5 w-3.5" />
                  {link.isPending ? "Getting link…" : "Copy invite link"}
                </Button>
              </div>
            </SheetHeader>

            <dl className="grid grid-cols-3 gap-3 rounded-lg border border-border p-3 text-center">
              {[
                ["Invited", c.invitedCount],
                ["Added to showcase", c.showcaseCount],
                ["Posted", c.contentCount],
              ].map(([label, value]) => (
                <div key={label as string}>
                  <dd className="text-lg font-semibold tabular-nums text-foreground">{value}</dd>
                  <dt className="text-[11px] text-muted-foreground">{label}</dt>
                </div>
              ))}
            </dl>

            {c.message && <p className="rounded-lg bg-muted/50 p-3 text-sm text-foreground">“{c.message}”</p>}

            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Products</h3>
              <ul className="divide-y divide-border rounded-lg border border-border">
                {c.products.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                    {p.imageUrl ? (
                      <img src={p.imageUrl} alt="" className="h-9 w-9 rounded-md object-cover" />
                    ) : (
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{p.title}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {p.commission}%{p.adsCommission ? ` · ads ${p.adsCommission}%` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Creators</h3>
              <ul className="divide-y divide-border rounded-lg border border-border">
                {c.creators.map((cr) => (
                  <li key={cr.openId} className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <UserAvatar src={cr.avatarUrl} name={cr.nickname || cr.username} className="h-8 w-8 text-[10px]" />
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() => setExpanded((e) => (e === cr.openId ? null : cr.openId))}
                        aria-expanded={expanded === cr.openId}
                      >
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-sm text-foreground">{cr.nickname || `@${cr.username}`}</span>
                          {cr.affiliate && (
                            <span className="rounded-full bg-brand-100 px-1.5 py-px text-[10px] font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-300">
                              Your affiliate
                            </span>
                          )}
                          <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", expanded === cr.openId && "rotate-180")} />
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {cr.showcaseProductCount > 0
                            ? `Added ${cr.showcaseProductCount} of ${c.products.length} products · ${
                                cr.contentProductCount > 0 ? `posted about ${cr.contentProductCount}` : "no posts yet"
                              }`
                            : "Has not accepted yet"}
                        </span>
                      </button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Message @${cr.username}`}
                        onClick={() => onMessage({ openId: cr.openId, username: cr.username, nickname: cr.nickname, avatarUrl: cr.avatarUrl })}
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                    </div>
                    {expanded === cr.openId && <CreatorPromotion collaborationId={c.id} openId={cr.openId} />}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
